<template>
    <div class="page" :class="{'surface fill-height': phoneOnly}">
        <v-container :class="{'px-0': phoneOnly}">
            <v-card :flat="phoneOnly" class="px-1 px-lg-3" :class="{'transparent': phoneOnly, 'soft-shadow': pcOnly}">
                <v-card-title class="d-flex flex-wrap align-center" style="row-gap: 8px;">
                    <span class="me-2">Quarterly Due Report</span>
                    <v-chip v-if="currentBatch" small label outlined color="primary">Current batch: {{ currentBatch }}</v-chip>
                </v-card-title>
                <v-card-subtitle :class="{'pe-3': phoneOnly}">
                    Submit a quarterly progress report for each of your active students in the current batch.
                </v-card-subtitle>

                <v-card-text :class="{'px-0': phoneOnly}">
                    <!-- Deadline banner -->
                    <v-alert
                        v-if="!canSubmit"
                        type="warning"
                        outlined
                        prominent
                        dense
                        class="mb-4"
                        :class="{'mx-4': phoneOnly}"
                        icon="mdi-clock-alert-outline"
                    >
                        <div class="font-weight-medium">Submission deadline has passed</div>
                        <div class="caption mt-1">
                            Reports must be submitted by the {{ submissionDeadlineDay }}<sup>th</sup> of the month. Your pending students are still listed below for your reference. Please contact your supervisor if you need to submit a report for this period.
                        </div>
                    </v-alert>

                    <v-progress-circular v-if="loading && !pending" indeterminate color="primary" class="d-block mx-auto my-6" />

                    <v-list v-else-if="pending && pending.length" max-width="850px" class="pa-0 ma-0 transparent">
                        <template v-for="(item, i) in pending">
                            <v-list-item :key="item.enrollment_id" class="my-1" :two-line="pcOnly" :three-line="phoneOnly">
                                <v-list-item-avatar tile class="primary me-3 me-lg-6" :size="phoneOnly ? 40 : 45">
                                    <v-icon color="white" style="opacity: 0.85" :small="phoneOnly">mdi-account-school-outline</v-icon>
                                </v-list-item-avatar>
                                <v-list-item-content class="py-2">
                                    <v-list-item-title class="font-weight-medium" style="white-space: normal;">{{ item.user && item.user.name }}</v-list-item-title>
                                    <v-list-item-subtitle class="details-text" style="white-space: normal;">
                                        {{ item.course && item.course.name }}
                                        <span v-if="item.category" class="muted--text"> · {{ item.category }}</span>
                                    </v-list-item-subtitle>
                                    <div v-if="phoneOnly" class="mt-2">
                                        <v-btn small color="primary" depressed :disabled="!canSubmit" @click="openForm(item)">
                                            <v-icon left small>{{ canSubmit ? 'mdi-plus' : 'mdi-lock-outline' }}</v-icon>
                                            {{ canSubmit ? 'Add Report' : 'Locked' }}
                                        </v-btn>
                                    </div>
                                </v-list-item-content>
                                <v-list-item-action v-if="pcOnly">
                                    <v-btn small color="primary" depressed :disabled="!canSubmit" @click="openForm(item)">
                                        <v-icon left small>{{ canSubmit ? 'mdi-plus' : 'mdi-lock-outline' }}</v-icon>
                                        {{ canSubmit ? 'Add Report' : 'Locked' }}
                                    </v-btn>
                                </v-list-item-action>
                            </v-list-item>
                            <v-divider v-if="i < pending.length - 1" :inset="pcOnly" style="opacity: 0.6" :key="'divider-' + item.enrollment_id"></v-divider>
                        </template>
                    </v-list>

                    <div v-else-if="!loading" :class="{'px-4': phoneOnly}">
                        <v-alert type="info" color="blue-grey" text>
                            No students require a quarterly report this month. You're all caught up!
                        </v-alert>
                    </div>
                </v-card-text>
            </v-card>
        </v-container>

        <quarterly-report-form-dialog
            v-model="showFormDialog"
            :enrollment="activeEnrollment"
            @submitted="onSubmitted"
        />
    </div>
</template>

<script>
import GlobalComputed from '@/helpers/global-computed';
import GlobalMethods from '@/helpers/global-methods';
import QuarterlyReportFormDialog from '@/components/quarterly-report/quarterly-report-form-dialog';

export default {
    name: 'QuarterlyReports',
    components: { QuarterlyReportFormDialog },
    data() {
        return {
            loading: false,
            showFormDialog: false,
            activeEnrollment: null,
        };
    },
    computed: {
        ...GlobalComputed,
        pending() { return this.$store.state.quarterlyReportsModule.pending; },
        currentBatch() { return this.$store.state.quarterlyReportsModule.currentBatch; },
        canSubmit() { return this.$store.state.quarterlyReportsModule.canSubmit; },
        submissionDeadlineDay() { return this.$store.state.quarterlyReportsModule.submissionDeadlineDay; },
    },
    methods: {
        ...GlobalMethods,
        loadData() {
            this.loading = true;
            this.$store.dispatch('loadQuarterlyPending')
                .catch(() => { /* handled silently; UI shows empty */ })
                .finally(() => { this.loading = false; });
        },
        openForm(item) {
            if (!this.canSubmit) {
                this.showMsg('The submission deadline has passed for this month.', 'error');
                return;
            }
            this.activeEnrollment = item;
            this.showFormDialog = true;
        },
        onSubmitted() {
            this.activeEnrollment = null;
            this.showMsg('Report submitted successfully.', 'success');
        },
    },
    created() {
        this.loadData();
    },
};
</script>
